<script>
export default { name: "ProgramLocationsSection" };
</script>

<script setup>
import { defineProps, reactive, onBeforeMount, defineEmits, unref, toRefs, computed } from "vue";
import { helpers, requiredIf, numeric, minValue } from "@vuelidate/validators";
import { differenceInDays, format, parse } from "date-fns";
import { useVModel } from "@vueuse/core";
import { cloneDeep, debounce } from "lodash";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import { ChevronRightIcon } from "@heroicons/vue/20/solid";
import { useVuelidate } from "@vuelidate/core";
import { ValidateEach } from "@vuelidate/components";
import PlusIcon from "@/components/shared/icons/PlusIcon.vue";
import BasicTextInput from "@/components/shared/input/BasicTextInput.vue";
import BasicCheckbox from "@/components/shared/input/BasicCheckbox.vue";
import Tooltip from "@/components/shared/Tooltip/PrimeVueTooltip.vue";
import CalendarInput from "@/components/shared/CalendarInput.vue";
import CheckboxSelect from "@/components/shared/select/CheckboxSelect.vue";
import SearchableSelect from "@/components/shared/select/SearchableSelect.vue";
import { verifyStartDateLowerThenEndDate } from "@/util/date";
import { useGetCityOptions } from "@/components/program-manager/sessions/composable";
import { getCityCountryOptionLabel } from "@/components/program-manager/sessions/utils";
import {
  sessionStatus,
  transportationProvidedOptions,
  sessionType,
  DURATION_TOOLTIP_MESSAGE,
} from "@/components/program-manager/sessions/constants";
import MeetingPointInfo from "@/components/MeetingPointInfo";
import { hasProgramsWritePermission } from "@/composables/authorization";

const props = defineProps({
  disableAdd: {
    type: Boolean,
    default: true,
  },
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  disablePriceDuration: {
    type: Boolean,
    default: false,
  },
});
const { disableAdd, disablePriceDuration } = toRefs(props);

const emit = defineEmits(["update:modelValue"]);
const programSession = useVModel(props, "modelValue", emit);

const deleteLocation = (index) => {
  programSession.session_travel_details.splice(index, 1);
};
const duplicateLocation = (index) => {
  const dup = reactive(cloneDeep(programSession.session_travel_details[index]));
  dup.id = undefined;
  programSession.session_travel_details.push(dup);
};
const addLocation = () => {
  programSession.session_travel_details.push({
    arrival_city_id: undefined,
    arrival_time_utc: undefined,
    arrival_date: undefined,
    departure_time_utc: undefined,
    departure_date: undefined,
    arrival_date_is_final: undefined,
    departure_date_is_final: undefined,
    arrival_airport: undefined,
    departure_airport: undefined,
    transportation_provided: undefined,
    meeting_point_name: undefined,
    meeting_point_address_line_1: undefined,
    meeting_point_address_line_2: undefined,
    meeting_point_city_id: undefined,
    meeting_point_postal_code: undefined,
    meeting_point_state: undefined,
    meeting_point_instructions: undefined,
    meeting_point_lat: undefined,
    meeting_point_long: undefined,
    meeting_point_final: false,
  });
};

const config = {
  dateFormat: "m/d/Y",
  parseDate: (dateStr) => {
    return parse(dateStr, "yyyy-MM-dd", new Date(), {});
  },
};

const requiredToEnrollNonVirtual = helpers.withMessage(
  "This field is required for Open Enrollment",
  requiredIf(function () {
    return unref(programSession)?.status_id === sessionStatus.openEnrollment && !isVirtual.value;
  })
);

const isVirtual = computed(() => {
  return unref(programSession)?.session_types?.find((val) => val.id == sessionType.virtual);
});

const checkStartEndDates = (value, { arrival_date: startDate, departure_date: endDate } = {}) =>
  unref(programSession)?.status_id === sessionStatus.draft
    ? true
    : verifyStartDateLowerThenEndDate(startDate, endDate);

const rules = {
  arrival_city: {
    required: requiredToEnrollNonVirtual,
  },
  arrival_time_utc: {},
  arrival_date: {
    required: requiredToEnrollNonVirtual,
    checkStartEndDates: helpers.withMessage("Arrival date should be before Departure date", checkStartEndDates),
  },
  departure_time_utc: {},
  departure_date: {
    required: requiredToEnrollNonVirtual,
    checkStartEndDates: helpers.withMessage("Arrival date should be before Departure date", checkStartEndDates),
  },
  arrival_date_is_final: {},
  departure_date_is_final: {},
  duration: {
    required: requiredToEnrollNonVirtual,
    numeric,
    minValueValue: minValue(0),
  },
  arrival_airport: {},
  departure_airport: {},
  transportation_provided: {},
  meeting_point_name: {},
  meeting_point_address_line_1: {},
  meeting_point_address_line_2: {},
  meeting_point_city_id: {},
  meeting_point_postal_code: {},
  meeting_point_state: {},
  meeting_point_instructions: {},
  meeting_point_lat: {},
  meeting_point_long: {},
  meeting_point_final: {},
};

//TODO: Add a helper function _.partial/curry to combine this common reused code block
const {
  execute: executeFetchCityOptions,
  state: cityOptions,
  isLoading: cityOptionsLoading,
  isReady: cityReady,
} = useGetCityOptions({ immediate: true, throwError: true }, { city_ascii: "chi" });

// const cityOptions = ref([]);
const fetchCityOptions = debounce(async (search, loading) => {
  if (search && search?.length > 2) {
    loading(true);
    try {
      await executeFetchCityOptions(0, {
        city_ascii: search,
        cancel: true,
      });
      loading(false);
    } catch (e) {
      if (e?.message !== "cancel") {
        loading(false);
      }
    }
  }
}, 500);

const travelDuration = computed(() => (location) => {
  return !!location?.departure_date && !!location?.arrival_date
    ? differenceInDays(new Date(location.departure_date), new Date(location.arrival_date))
    : undefined;
});

const handleMeetingPointChange = (index) => (updatedPoint) => {
  programSession.value.session_travel_details[index] = updatedPoint;
};

const disabledMeetingPointSection = computed(() => !hasProgramsWritePermission());

// FIXME: If vuelidate fixes https://github.com/vuelidate/vuelidate/issues/764 looks like its in maintained mode so seems unlikely
let v$ = {};
onBeforeMount(() => {
  v$ = useVuelidate({}, programSession, {
    $registerAs: "ProgramLocationsSection",
    $lazy: true,
  });
});
</script>
<template>
  <Disclosure as="div" :default-open="true">
    <DisclosureButton
      v-slot="{ open }"
      class="text-sm font-semibold text-gray-900 uppercase flex items-center cursor-pointer"
    >
      <slot name="fieldLabel">
        <ChevronRightIcon :class="[{ 'rotate-90 transform': open }, 'h-6 w-6 inline-block']" /><span
          >Program Locations</span
        >
      </slot>
    </DisclosureButton>
    <DisclosurePanel>
      <ValidateEach
        v-for="(location, index) in programSession.session_travel_details"
        :key="index"
        :state="location"
        :rules="rules"
      >
        <template #default="{ v }">
          <div class="border-2 border-dashed border-gray-300 mb-4">
            <div class="flex p-4 bg-white items-center">
              <div class="font-semibold text-gray-700">Location {{ index + 1 }}</div>
              <div
                v-if="!disableAdd"
                class="ml-auto uppercase text-xs font-semibold text-teal-900 mr-10"
                @click="() => duplicateLocation(index)"
              >
                duplicate
              </div>
              <div v-if="!disableAdd" @click="() => deleteLocation(index)">
                <i class="fa fa-trash text-gray-650"></i>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-x-20 gap-y-4 bg-blue-300 p-4">
              <SearchableSelect
                v-model="location.arrival_city"
                class="col-span-2"
                :options="
                  location?.arrival_city
                    ? [...(cityOptions?.items || []), location?.arrival_city]
                    : cityOptions?.items || []
                "
                :vuelidate-instance="v.arrival_city"
                placeholder="City, Country"
                :filterable="false"
                label="city"
                :get-option-label="getCityCountryOptionLabel"
                :loading="cityOptionsLoading || !cityReady"
                @search="fetchCityOptions"
              >
                <template #fieldLabel>
                  <label class="common-label-text"
                    ><span v-if="!isVirtual" class="text-red-100 pr-2">**</span>Location</label
                  >
                </template>
              </SearchableSelect>
              <CalendarInput
                placeholder="Select date"
                name="date"
                :config="config"
                :vuelidate-instance="v.arrival_date"
                :model-value="location.arrival_date || null"
                @onChange="
                  (date) => (location.arrival_date = date && date[0] ? format(new Date(date[0]), 'yyyy-MM-dd') : '')
                "
              >
                <template #fieldLabel>
                  <label class="common-label-text"
                    ><span v-if="!isVirtual" class="text-red-100 pr-2">**</span>Arrival Date</label
                  >
                </template>
              </CalendarInput>
              <CalendarInput
                placeholder="Select date"
                name="date"
                :config="config"
                :vuelidate-instance="v.departure_date"
                :model-value="location.departure_date || null"
                @onChange="
                  (date) => (location.departure_date = date && date[0] ? format(new Date(date[0]), 'yyyy-MM-dd') : '')
                "
              >
                <template #fieldLabel>
                  <label class="common-label-text"
                    ><span v-if="!isVirtual" class="text-red-100 pr-2">**</span>Departure Date</label
                  >
                </template>
              </CalendarInput>
              <BasicCheckbox
                v-model="location.arrival_date_is_final"
                class="my-auto"
                :vuelidate-instance="v.arrival_date_is_final"
                field-label="Arrival Date is final"
              />
              <BasicCheckbox
                v-model="location.departure_date_is_final"
                class="my-auto"
                :vuelidate-instance="v.departure_date_is_final"
                field-label="Departure Date is final"
              />
              <BasicTextInput
                v-model="location.duration"
                field-label="Duration"
                type="number"
                min="0"
                :disabled="disablePriceDuration"
                :vuelidate-instance="v.duration"
              >
                <template #fieldLabel>
                  <p class="common-label-text">
                    <span v-if="!isVirtual" class="text-red-100 pr-2">**</span>
                    Price Duration
                    <span>
                      <Tooltip :message="DURATION_TOOLTIP_MESSAGE" />
                    </span>
                  </p>
                </template>
              </BasicTextInput>
              <div v-if="!!travelDuration(location)">
                <p class="common-label-text">Calculated Duration</p>
                <p :data-testid="`travel-duration-${index}`">
                  {{ travelDuration(location) }}
                </p>
              </div>
              <BasicTextInput
                v-model="location.arrival_time_utc"
                type="time"
                class="rounded-sm border-gray-300"
                :vuelidate-instance="v.arrival_time_utc"
                field-label="Arrival Time"
              />
              <BasicTextInput
                v-model="location.departure_time_utc"
                type="time"
                class="rounded-sm border-gray-300"
                :vuelidate-instance="v.departure_time_utc"
                field-label="Departure Time"
              />
              <BasicTextInput
                v-model="location.arrival_airport"
                input-classes="h-10 w-full form-input"
                :vuelidate-instance="v.arrival_airport"
                field-label="Arrival Airport"
              />
              <BasicTextInput
                v-model="location.departure_airport"
                input-classes="h-10 w-full form-input"
                :vuelidate-instance="v.departure_airport"
                field-label="Departure Airport"
              />
              <CheckboxSelect
                v-model="location.transportation_provided"
                :vuelidate-instance="v.transportation_provided"
                field-label="Transportation Provided"
                :options="transportationProvidedOptions"
                :multiple="true"
                :deselect-from-dropdown="true"
                :close-on-select="false"
                placeholder="Pick a value"
                label="name"
              />
              <MeetingPointInfo
                :meeting-point-location="location"
                :disableInputs="disabledMeetingPointSection"
                @update="(updated) => handleMeetingPointChange(index)(updated)"
              />
            </div>
          </div>
        </template>
      </ValidateEach>
      <div
        v-if="!disableAdd"
        class="mt-4 bg-white border-2 border-dashed border-gray-300 rounded cursor-pointer"
        @click="addLocation"
      >
        <div class="flex items-center m-5 p-1">
          <PlusIcon class="mr-2 text-teal-900 w-10 h-10" />
          <label class="common-label-text inline">Add another</label>
        </div>
      </div>
    </DisclosurePanel>
  </Disclosure>
</template>
